import { lazy, Suspense } from "react";
import { createBrowserRouter, RouterProvider, Outlet } from "react-router-dom";
import { LoadingPage } from "../components/Loading";
import ErrorBoundary from "../components/ErrorBoundary";
import Layout from "../components/Layout";
import AuthCallbackHandler from "../components/AuthCallbackHandler";
import { RealmProvider, useRealm } from "../context/RealmContext";
import { RealmError } from "../components/RealmError";
import { ROUTES } from "./constants";

const HomePage = lazy(() => import("../pages/HomePage"));
const NotFoundPage = lazy(() => import("../pages/NotFoundPage"));

const MigrationStartPage = lazy(
  () => import("../pages/migration/MigrationStartPage")
);
const WelcomePage = lazy(() => import("../pages/migration/WelcomePage"));
const VerificationPage = lazy(
  () => import("../pages/migration/VerificationPage")
);
const EmailConfirmPage = lazy(
  () => import("../pages/migration/EmailConfirmPage")
);
const SuccessPage = lazy(() => import("../pages/migration/SuccessPage"));
const TokenExpiredPage = lazy(
  () => import("../pages/migration/TokenExpiredPage")
);
const AlreadyMigratedPage = lazy(
  () => import("../pages/migration/AlreadyMigratedPage")
);
const EmailEntryPage = lazy(() => import("../pages/migration/EmailEntryPage"));
const SetPasswordPage = lazy(
  () => import("../pages/migration/SetPasswordPage")
);
const MigrationSignInPage = lazy(
  () => import("../pages/migration/MigrationSignInPage")
);
const ConflictResolutionPage = lazy(
  () => import("../pages/migration/ConflictResolutionPage")
);
const AlternativeEmailPage = lazy(
  () => import("../pages/migration/AlternativeEmailPage")
);
const LinkCallbackPage = lazy(
  () => import("../pages/migration/LinkCallbackPage")
);

const RegistrationStartPage = lazy(
  () => import("../pages/registration/RegistrationStartPage")
);
const RegistrationVerifyPage = lazy(
  () => import("../pages/registration/RegistrationVerifyPage")
);
const RegistrationPasswordPage = lazy(
  () => import("../pages/registration/RegistrationPasswordPage")
);
const RegistrationConflictPage = lazy(
  () => import("../pages/registration/RegistrationConflictPage")
);
const RegistrationSuccessPage = lazy(
  () => import("../pages/registration/RegistrationSuccessPage")
);

import AuthProvider from "../auth/AuthProvider";
import { useAuthApiClient } from "../hooks/useAuthApiClient";
import AuthGuard from "../components/AuthGuard";

const MyIdentityPage = lazy(() => import("../pages/MyIdentityPage"));

/**
 * Root wrapper that resolves the realm from hostname.
 * Shows loading while the backend is resolving, error if resolution fails.
 */
function RealmGate() {
  const { realmId, hostname, error, isLoading } = useRealm();
  if (isLoading) {
    return <LoadingPage />;
  }
  if (!realmId) {
    return <RealmError hostname={hostname} error={error || ""} />;
  }
  return <Outlet />;
}

function AuthenticatedRoutes() {
  return (
    <AuthProvider>
      <AuthenticatedRoutesInner />
    </AuthProvider>
  );
}

function AuthenticatedRoutesInner() {
  useAuthApiClient();
  return <Outlet />;
}

/**
 * The wizard is public, but linking an existing Sovra login is proven by an
 * Auth0 sign-in, so the migration routes live under the OIDC provider too.
 */
function MigrationRoutes() {
  return (
    <AuthProvider>
      <Outlet />
    </AuthProvider>
  );
}

function SuspenseWrapper() {
  return (
    <Suspense fallback={<LoadingPage />}>
      <Outlet />
    </Suspense>
  );
}

export const router = createBrowserRouter([
  {
    path: ROUTES.HOME,
    element: (
      <ErrorBoundary>
        <RealmProvider>
          <AuthCallbackHandler>
            <Layout />
          </AuthCallbackHandler>
        </RealmProvider>
      </ErrorBoundary>
    ),
    children: [
      {
        element: <RealmGate />,
        children: [
          {
            element: <SuspenseWrapper />,
            children: [
              {
                index: true,
                element: <HomePage />,
              },
              // Migration wizard routes
              {
                element: <MigrationRoutes />,
                children: [
                  {
                    path: "migration/start",
                    element: <MigrationStartPage />,
                  },
                  {
                    path: "migration/welcome",
                    element: <WelcomePage />,
                  },
                  {
                    path: "migration/verify",
                    element: <VerificationPage />,
                  },
                  {
                    path: "migration/email-confirm",
                    element: <EmailConfirmPage />,
                  },
                  {
                    path: "migration/success",
                    element: <SuccessPage />,
                  },
                  {
                    path: "migration/expired",
                    element: <TokenExpiredPage />,
                  },
                  {
                    path: "migration/already-migrated",
                    element: <AlreadyMigratedPage />,
                  },
                  {
                    path: "migration/email-entry",
                    element: <EmailEntryPage />,
                  },
                  {
                    path: "migration/set-password",
                    element: <SetPasswordPage />,
                  },
                  {
                    path: "migration/sign-in",
                    element: <MigrationSignInPage />,
                  },
                  {
                    path: "migration/conflict",
                    element: <ConflictResolutionPage />,
                  },
                  {
                    path: "migration/alternative-email",
                    element: <AlternativeEmailPage />,
                  },
                  {
                    path: "migration/link-callback",
                    element: <LinkCallbackPage />,
                  },
                  // Registration wizard: same session and Auth0 sign-in as the
                  // migration, its own pages (the link callback stays shared,
                  // its path is registered in the tenant)
                  {
                    path: "registration/start",
                    element: <RegistrationStartPage />,
                  },
                  {
                    path: "registration/verify",
                    element: <RegistrationVerifyPage />,
                  },
                  {
                    path: "registration/password",
                    element: <RegistrationPasswordPage />,
                  },
                  {
                    path: "registration/conflict",
                    element: <RegistrationConflictPage />,
                  },
                  {
                    path: "registration/success",
                    element: <RegistrationSuccessPage />,
                  },
                ],
              },
              // Authenticated user routes — My Identity
              {
                path: "identity",
                element: <AuthenticatedRoutes />,
                children: [
                  {
                    element: <AuthGuard />,
                    children: [
                      {
                        index: true,
                        element: <MyIdentityPage />,
                      },
                    ],
                  },
                ],
              },
              {
                path: "*",
                element: <NotFoundPage />,
              },
            ],
          },
        ],
      },
    ],
  },
]);

export function AppRouter() {
  return <RouterProvider router={router} />;
}

export default AppRouter;
